import { Fragment, useEffect } from 'react';
import { Header, HEADER_HEIGHT } from './Header';
import { useParams } from 'react-router';
import { NotFound } from '@framework/ui/pages/NotFound';
import { useCurrentUserId } from '@framework/auth';
import { NotFoundPage } from '@user/pages/NotFoundPage';
import { Loading } from '@framework/ui/atoms/Loading';
import { GroupContent } from './GroupContent';
import { usePageViewLogger } from '@framework/action-log';
import type { GroupEntity } from '@group/domain';
import { useGroupEntity } from '@group/hooks';
import type { GroupId, UserId } from 'balus-common';
import { UserSurveyModal } from '@user/UserSurveyAnswer/UserSurveyModal';

export const GroupPage: React.FC = () => {
  const currentUserId = useCurrentUserId();
  const { groupId } = useParams<{ groupId: string }>();

  return currentUserId && groupId ? (
    <>
      <GroupPageContent groupId={groupId} currentUserId={currentUserId} />
      <UserSurveyModal groupId={groupId} currentUserId={currentUserId} />
    </>
  ) : (
    <NotFoundPage />
  );
};

const useGroupPageView = (group: GroupEntity | null): void => {
  const pageViewLogger = usePageViewLogger('GroupPage');

  useEffect(() => {
    if (!group) return;

    pageViewLogger.landing({
      groupId: group.id,
      groupName: group.name.toString(),
    });

    return () => {
      pageViewLogger.leaving();
    };
  }, [group, pageViewLogger]);
};

type Props = {
  currentUserId: UserId;
  groupId: GroupId;
};

const GroupPageContent: React.FC<Props> = ({ currentUserId, groupId }: Props) => {
  const { group, loading } = useGroupEntity(groupId);

  useGroupPageView(group);

  return (
    <Fragment>
      {loading ? (
        <Loading />
      ) : group ? (
        <>
          <Header currentGroup={group} currentUserId={currentUserId} />
          <div className="overflow-y-auto px-10 py-3" style={{ height: `calc(100vh - ${HEADER_HEIGHT}px)` }}>
            <GroupContent currentUserId={currentUserId} group={group} />
          </div>
        </>
      ) : (
        <NotFound />
      )}
    </Fragment>
  );
};
